import axios from 'axios';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { BASE_URL, POST_PRIVILAGE_CARD } from '../../utlilities/config';
import { useState } from 'react';
import { ToastContainer, toast, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FiX, FiDollarSign, FiCreditCard, FiAlignLeft, FiPercent } from 'react-icons/fi';

// Loading spinner component
const Spinner = () => (
  <div className="flex justify-center items-center h-full w-full absolute inset-0 bg-white bg-opacity-80 z-50">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
  </div>
);

const FormField = ({ label, required, error, touched, children }) => (
  <div className="w-full">
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
    {touched && error && (
      <p className="mt-1 text-sm text-red-500">{error}</p>
    )}
  </div>
);

const NewCard = ({ setCardModal, fetchData, fetchData1 }) => {
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('accessToken');

  const validationSchema = Yup.object({
    name: Yup.string().required('Card name is required'),
    description: Yup.string(),
    amount: Yup.number()
      .required('Amount is required')
      .min(0, 'Amount cannot be negative'),
    isAvailableForAll: Yup.boolean(),
    discountPercentage: Yup.number()
      .when('isAvailableForAll', {
        is: true,
        then: (schema) =>
          schema
            .required('Discount is required')
            .min(1, 'Minimum 1%')
            .max(100, 'Maximum 100%'),
        otherwise: (schema) => schema.notRequired(),
      }),
  });
  

  const formik = useFormik({
    initialValues: {
      name: '',
      description: '',
      amount: '',
      isAvailableForAll: false,
      discountPercentage: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const response = await axios.post(`${BASE_URL}${POST_PRIVILAGE_CARD}`, {}, {
          headers: {
            'XApiKey': process.env.REACT_APP_API_KEY,
            'cardName': values.name,
            'description': values.description,
            'amount': values.amount,
            'discPerc': values.discountPercentage,
            'Authorization': `Bearer ${token}`,
          },
        });
        if (response.status === 200) {
          toast.success('Card added successfully!', {
            autoClose: 1000,
          });
          fetchData();
          fetchData1();
          setCardModal(false);
        }
      } catch (error) {
        console.error('Error submitting the form:', error);
        toast.error('Failed to add card. Please try again.', {
          autoClose: 1000,
        });
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <ToastContainer
        position="top-center"
        autoClose={1000}
        limit={1}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />
      
      {loading && <Spinner />}
      
      <div className="bg-white w-2/3 max-w-3xl rounded-2xl shadow-2xl transform transition-all duration-300 ease-out animate-fade-in overflow-hidden">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-5 flex justify-between items-center sticky top-0 z-20">
          <h2 className="text-xl font-semibold text-white flex items-center">
            <FiCreditCard className="mr-2" /> Add New Card
          </h2>
          <button 
            onClick={() => setCardModal(false)}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors duration-200"
            aria-label="Close modal"
          >
            <FiX size={20} />
          </button>
        </div>
        
        {/* Form */}
        <form onSubmit={formik.handleSubmit} className="p-6 space-y-6">
          {/* Card details section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField 
              label="Card Name" 
              required={true}
              error={formik.errors.name}
              touched={formik.touched.name}
            >
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500">
                  <FiCreditCard />
                </div>
                <input
                  type="text"
                  name="name"
                  placeholder="Premium Health Card"
                  className={`w-full pl-10 pr-3 py-3 border rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200 ${
                    formik.touched.name && formik.errors.name
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                      : 'border-gray-200 focus:border-blue-500 focus:ring-blue-200'
                  }`}
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
            </FormField>

            <FormField 
              label="Card Description" 
              required={true}
              error={formik.errors.description}
              touched={formik.touched.description}
            >
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500">
                  <FiAlignLeft />
                </div>
                <input
                  type="text"
                  name="description"
                  placeholder="Special benefits for premium members"
                  className={`w-full pl-10 pr-3 py-3 border rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200 ${
                    formik.touched.description && formik.errors.description
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                      : 'border-gray-200 focus:border-blue-500 focus:ring-blue-200'
                  }`}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
            </FormField>
          </div>

          {/* Amount and discount section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField 
              label="Card Amount" 
              required={true}
              error={formik.errors.amount}
              touched={formik.touched.amount}
            >
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500">
                  <FiDollarSign />
                </div>
                <input
                  type="number"
                  name="amount"
                  placeholder="100"
                  className={`w-full pl-10 pr-3 py-3 border rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200 ${
                    formik.touched.amount && formik.errors.amount
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                      : 'border-gray-200 focus:border-blue-500 focus:ring-blue-200'
                  }`}
                  value={formik.values.amount}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
            </FormField>

            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  id="isAvailableForAll"
                  type="checkbox"
                  name="isAvailableForAll"
                  checked={formik.values.isAvailableForAll}
                  onChange={formik.handleChange}
                  className="w-5 h-5 text-blue-500 rounded focus:ring-blue-500"
                />
                <label htmlFor="isAvailableForAll" className="ml-2 text-sm font-medium text-gray-700">
                  Available for all patients
                </label>
              </div>

              {formik.values.isAvailableForAll && (
                <FormField 
                  label="Discount Percentage (%)" 
                  required={true}
                  error={formik.errors.discountPercentage}
                  touched={formik.touched.discountPercentage}
                >
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500">
                      <FiPercent />
                    </div>
                    <input
                      type="number"
                      name="discountPercentage"
                      placeholder="10"
                      className={`w-full pl-10 pr-3 py-3 border rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200 ${
                        formik.touched.discountPercentage && formik.errors.discountPercentage
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                          : 'border-gray-200 focus:border-blue-500 focus:ring-blue-200'
                      }`}
                      value={formik.values.discountPercentage}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </div>
                </FormField>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              type="submit"
              className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg hover:from-indigo-700 hover:to-purple-700 focus:ring-4 focus:ring-indigo-300 transition-all duration-200 shadow-md hover:shadow-lg flex items-center"
            >
              <FiX className="mr-2" /> Save Card
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewCard;

